import React from 'react';
import { Download, FileText, Paperclip } from 'lucide-react';
import { ChatMessage, formatMediaSize } from './messageTypes';

/**
 * Renders an attachment that really exists on disk.
 *
 * Before this, every incoming photo, file and voice note became text like
 * "[Изображение]" and the file was never downloaded, so a manager could not see
 * what the customer had sent. The backend now stores the file and fills
 * `mediaUrl` / `mediaType` / `mediaName` / `mediaSize`.
 */

interface MessageAttachmentProps {
  message: Pick<ChatMessage, 'mediaUrl' | 'mediaType' | 'mediaName' | 'mediaSize'>;
}

export const MessageAttachment: React.FC<MessageAttachmentProps> = ({ message }) => {
  const url = message.mediaUrl;
  if (!url) return null;

  const name = message.mediaName || 'Вложение';
  const size = formatMediaSize(message.mediaSize);

  if (message.mediaType === 'image') {
    return (
      <a className="msg-media msg-media-image" href={url} target="_blank" rel="noopener noreferrer" title={name}>
        <img src={url} alt={name} loading="lazy" />
      </a>
    );
  }

  if (message.mediaType === 'audio') {
    return (
      <div className="msg-media msg-media-audio">
        <audio controls preload="metadata" src={url} />
        <span className="msg-media-caption">{name}{size ? ` · ${size}` : ''}</span>
      </div>
    );
  }

  if (message.mediaType === 'video') {
    return (
      <div className="msg-media msg-media-video">
        <video controls preload="metadata" src={url} />
        <span className="msg-media-caption">{name}{size ? ` · ${size}` : ''}</span>
      </div>
    );
  }

  return (
    <a className="msg-media msg-media-file" href={url} target="_blank" rel="noopener noreferrer" download={name}>
      <span className="msg-media-file-icon" aria-hidden="true">
        {message.mediaType === 'document' ? <FileText size={16} /> : <Paperclip size={16} />}
      </span>
      <span className="msg-media-file-body">
        <span className="msg-media-file-name">{name}</span>
        {size && <span className="msg-media-file-size">{size}</span>}
      </span>
      <Download size={14} aria-hidden="true" />
    </a>
  );
};
